import React, { useState } from "react";
import { postRide } from "../utils/api";
import "../css/RideForm.css";
import AutocompleteInput from "../components/AutocompleteInput";
import RouteSelector from "../components/RouteSelector";
import InterstitialAd from "./ads/InterstitialAd";

export default function RideForm({ onSubmit }) {
  const [form, setForm] = useState({
    from: "",
    to: "",
    date: "",
    time: "",
    seatsAvailable: 1,
    pricePerSeat: "",
    vehicle: "Car", 
    vehicleName: "",
    description: "",
  });

  const [selectedRoute, setSelectedRoute] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const [isAdOpen, setIsAdOpen] = useState(false);
  const [completedRide, setCompletedRide] = useState(null);

  // ✅ THE FIX: Strictly checks that From, To, and Vehicle Name all have actual text
  const canShowMap = Boolean(
    form.from && form.from.trim().length >= 7 &&
    form.to && form.to.trim().length >= 7 &&
    form.vehicleName && form.vehicleName.trim().length >= 1
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSwap = () => {
    setForm((prev) => ({ ...prev, from: prev.to, to: prev.from }));
    setSelectedRoute(null); // Reset route when swapped
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccessMsg("");

    if (!form.from || !form.to) return setError("Please enter locations.");
    if (!selectedRoute) return setError("Please select a route on the map.");

    setLoading(true);
    try {
      const token = localStorage.getItem("travelo_token");
      
      const combinedVehicle = form.vehicleName.trim() 
        ? `${form.vehicleName.trim()} (${form.vehicle})` 
        : `(${form.vehicle})`;

      const payload = {
        ...form,
        vehicle: combinedVehicle,
        seatsAvailable: Number(form.seatsAvailable),
        pricePerSeat: Number(form.pricePerSeat || 0),
        route: selectedRoute,
      };

      const data = await postRide(payload, token);

      alert("✅ Ride published successfully!");
      setForm({ from: "", to: "", date: "", time: "", seatsAvailable: 1, pricePerSeat: "", vehicle: "Car", vehicleName: "", description: "" });
      setSelectedRoute(null);

      // ✅ 4. SAVE THE RIDE DATA & TRIGGER THE AD POPUP!
      setCompletedRide(data.ride);
      setIsAdOpen(true);

    } catch (err) {
      setError(err.response?.data?.message || "Failed to post ride");
    } finally {
      setLoading(false);
    }
  };

  // This runs when the user clicks the "✕ Close" button on your ad.
  const handleCloseAdAndProceed = () => {
    setIsAdOpen(false); // Hide the ad
    
    // Now trigger the parent component's onSubmit so it navigates back to home!
    if (onSubmit && completedRide) {
      onSubmit(completedRide);
    }
  };

  return (
    <div className="ride-form-wrapper">
      <h2>Publish a Ride</h2>

      <form onSubmit={handleSubmit} className="ride-form">
        {error && <div className="error">{error}</div>}
        {successMsg && <div className="success">{successMsg}</div>}

        {/* 1. LOCATION ROW (From [Swap] To) */}
        <div className="row swap-row">
          <div className="half">
            <label>From</label>
            <AutocompleteInput
              value={form.from}
              placeholder="Leaving from"
              onChange={(val) => {
                setForm(f => ({ ...f, from: typeof val === 'string' ? val : val.target.value }));
                setSelectedRoute(null);
              }}
            />
          </div>

          <button type="button" className="swap-button hide-on-mobile" onClick={handleSwap}>
            ⇄
          </button>

          <div className="half">
            <label>To</label>
            <AutocompleteInput
              value={form.to}
              placeholder="Going to"
              onChange={(val) => {
                setForm(f => ({ ...f, to: typeof val === 'string' ? val : val.target.value }));
                setSelectedRoute(null);
              }}
            />
          </div>
        </div>

        {/* 2. VEHICLE ROW (Name & Type in one row) */}
        <div className="row">
          <div className="half">
            <label>Vehicle Name</label>
            <input
              name="vehicleName"
              placeholder="e.g. Tata Nexon"
              value={form.vehicleName}
              onChange={handleChange}
            />
          </div>
          <div className="half">
            <label>Vehicle Type</label>
            <div className="vehicle-pills">
              {['Car', 'Bike', 'SUV', 'Van'].map((type) => (
                <button
                  key={type}
                  type="button"
                  className={`pill-btn ${form.vehicle === type ? 'active' : ''}`}
                  onClick={() => setForm(f => ({ ...f, vehicle: type }))}
                >
                  {type}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* 3. MAP ROW (Loads Automatically based on canShowMap logic) */}
        <div className={`map-box ${canShowMap ? 'active' : 'hidden'}`}>
          {canShowMap ? (
            <RouteSelector from={form.from} to={form.to} onSelect={setSelectedRoute} />
          ) : (
            <div className="map-placeholder">📍 Enter full locations and vehicle name to see route</div>
          )}
        </div>

        {/* 4. LOGISTICS ROW (Responsive 2x2 Grid on Mobile) */}
        <div className="logistics-row">
          
          {/* Group 1: Date & Time */}
          <div className="logistics-group">
            <div className="input-box">
              <label>Date</label>
              <input name="date" type="date" value={form.date} onChange={handleChange} required />
            </div>
            <div className="input-box">
              <label>Time</label>
              <input name="time" type="time" value={form.time} onChange={handleChange} required />
            </div>
          </div>

          {/* Group 2: Seats & Price */}
          <div className="logistics-group">
            <div className="input-box">
              <label>Seats</label>
              <input name="seatsAvailable" type="number" min="1" value={form.seatsAvailable} onChange={handleChange} />
            </div>
            <div className="input-box">
              <label>Price (₹)</label>
              <input name="pricePerSeat" type="number" min="0" value={form.pricePerSeat} onChange={handleChange} />
            </div>
          </div>

        </div>

        <button type="submit" disabled={loading || !selectedRoute} className="button">
          {loading ? "Publishing..." : "Publish Ride"}
        </button>
      </form>

             {/* ✅ THE AD: Show after successful submission, then proceed to next step */}
      <InterstitialAd isOpen={isAdOpen} onClose={handleCloseAdAndProceed} />
    </div>
  );
}